// Vue & Plugins
import { computed, MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { useQuery, keepPreviousData, queryOptions, skipToken, useInfiniteQuery } from '@tanstack/vue-query'

// Models
import { Site, SiteID } from '../../sites/models/site'
import { Pagination } from '../../common/models/pagination'
import { Employee, EmployeeID } from '../models/employee'

// Services
import { EligibleSearchForm, EmployeeSearchForm, EmployeeSortBy, fetch, fetchEligible, HasNewAppLoginFilter, LastActiveStatus, show } from '../employeeService'
import { DateTime } from 'luxon'
import { AreaID } from '../../areas/models/area'
import { ShiftID } from '../../shifts/models/shift'
import { Skill, SkillID } from '../../skills/models/skill'
import { JobRole, JobRoleID } from '../../job_roles/models/jobRole'
import { Building } from '../../buildings/models/building'
import { Group } from '../../groups/models/group'
import { Tag, TagMatchMode } from '../../tags/models/tag'
import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'

export interface EmployeeFilters {
    name?: MaybeRefOrGetter<string>
    skill?: MaybeRefOrGetter<Skill | null>
    skills?: MaybeRefOrGetter<Skill[] | null>
    jobRole?: MaybeRefOrGetter<JobRole | null>

    group?: MaybeRefOrGetter<Group | null>

    building?: MaybeRefOrGetter<Building | null>
    role?: MaybeRefOrGetter<string | undefined>
    hasAccount?: MaybeRefOrGetter<'all' | 'yes' | 'no'>
    hasNewAppLogin?: MaybeRefOrGetter<HasNewAppLoginFilter | null>
    lastActiveStatus?: MaybeRefOrGetter<LastActiveStatus | null>

    showArchived?: MaybeRefOrGetter<boolean>

    tags?: MaybeRefOrGetter<Tag[]>
    tagIds?: MaybeRefOrGetter<Tag['id'][]>
    tagNames?: MaybeRefOrGetter<Tag['name'][]>
    tagMatchMode?: MaybeRefOrGetter<TagMatchMode>

    sortBy?: MaybeRefOrGetter<EmployeeSortBy | null>
    sortDirection?: MaybeRefOrGetter<'asc' | 'desc'>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>

    status?: MaybeRefOrGetter<'all' | ShiftOfferStatus>

    availableFrom?: MaybeRefOrGetter<DateTime | null>
    availableTo?: MaybeRefOrGetter<DateTime | null>
}

interface EligibleFilters {
    skill?: MaybeRefOrGetter<Skill | null>

    group?: MaybeRefOrGetter<Group | null>

    shiftId?: MaybeRefOrGetter<ShiftID | null | undefined>
    areaId?: MaybeRefOrGetter<AreaID | null | undefined>
    skillIds?: MaybeRefOrGetter<SkillID[] | null | undefined>
    jobRoleId?: MaybeRefOrGetter<JobRoleID | null | undefined>
    startDate?: MaybeRefOrGetter<DateTime | null | undefined>
    endDate?: MaybeRefOrGetter<DateTime | null | undefined>

    name?: MaybeRefOrGetter<string>

    tags?: MaybeRefOrGetter<Tag[]>
    tagIds?: MaybeRefOrGetter<Tag['id'][]>
    tagNames?: MaybeRefOrGetter<Tag['name'][]>
    tagMatchMode?: MaybeRefOrGetter<TagMatchMode>

    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>

    status?: MaybeRefOrGetter<ShiftOfferStatus>
}

export const employeeKeys = {
    all: ['employees'] as const,
    lists: () => [...employeeKeys.all, 'list'] as const,
    list: (siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EmployeeFilters) => [...employeeKeys.lists(), siteId, {
        name: filters.name,
        building: filters.building,
        skill: filters.skill,
        skills: filters.skills,
        jobRole: filters.jobRole,
        group: filters.group,
        roles: filters.role,
        hasAccount: filters.hasAccount,
        hasNewAppLogin: filters.hasNewAppLogin,
        lastActiveStatus: filters.lastActiveStatus,
        showArchived: filters.showArchived,
        tags: filters.tags,
        tagIds: filters.tagIds,
        tagNames: filters.tagNames,
        tagMatchMode: filters.tagMatchMode,
        availableFrom: filters.availableFrom,
        availableTo: filters.availableTo,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        perPage: filters.perPage,
        page: filters.page
    }] as const,
    listInfinite: (siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EmployeeFilters) => [...employeeKeys.lists(), siteId, 'infinite', {
        name: filters.name,
        building: filters.building,
        skill: filters.skill,
        skills: filters.skills,
        jobRole: filters.jobRole,
        group: filters.group,
        roles: filters.role,
        hasAccount: filters.hasAccount,
        hasNewAppLogin: filters.hasNewAppLogin,
        lastActiveStatus: filters.lastActiveStatus,
        showArchived: filters.showArchived,
        tags: filters.tags,
        tagIds: filters.tagIds,
        tagNames: filters.tagNames,
        tagMatchMode: filters.tagMatchMode,
        availableFrom: filters.availableFrom,
        availableTo: filters.availableTo,
        sortBy: filters.sortBy,
        sortDirection: filters.sortDirection,
        perPage: filters.perPage,
        page: filters.page
    }] as const,
    details: () => [...employeeKeys.all, 'detail'] as const,
    detail: (siteId: MaybeRefOrGetter<SiteID | undefined>, id: MaybeRefOrGetter) => [...employeeKeys.details(), siteId, id] as const,
    checkArchive: (siteId: MaybeRefOrGetter<SiteID | undefined>, id: MaybeRefOrGetter) => [...employeeKeys.details(), siteId, id, 'checkArchive'] as const,
    listEligibleForShift: (siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EligibleFilters) => [...employeeKeys.lists(), siteId, { name: filters.name, skill: filters.skill, group: filters.group, shiftId: filters.shiftId, areaId: filters.areaId, skillIds: filters.skillIds, jobRoleId: filters.jobRoleId, startDate: filters.startDate, endDate: filters.endDate, tags: filters.tags, tagIds: filters.tagIds, tagNames: filters.tagNames, tagMatchMode: filters.tagMatchMode }] as const,
    listByShiftOnly: (siteId: MaybeRefOrGetter<SiteID | undefined>, shiftId: MaybeRefOrGetter<ShiftID>) => [...employeeKeys.lists(), siteId, shiftId] as const,
    listByShift: (siteId: MaybeRefOrGetter<SiteID | undefined>, shiftId: MaybeRefOrGetter<ShiftID | null | undefined>, filters: EmployeeFilters) => [...employeeKeys.lists(), siteId, shiftId, { name: filters.name, perPage: filters.perPage, page: filters.page, status: filters.status }] as const,
}

const filtersToForm = (filters: EmployeeFilters): EmployeeSearchForm => {
    const form: EmployeeSearchForm = {
        name: toValue(filters.name),
        sortBy: toValue(filters.sortBy),
        sortDirection: toValue(filters.sortDirection),
        perPage: toValue(filters.perPage) ?? 15,
        page: toValue(filters.page) ?? 1,
    }

    const building = toValue(filters.building)
    if (building) {
        form.buildingId = building.id
    }

    const skill = toValue(filters.skill)
    if (skill) {
        form.skillId = skill.id
    }

    const skills = toValue(filters.skills)
    if (skills && skills.length > 0) {
        form.skillIds = skills.map(skill => skill.id)
    }

    const jobRole = toValue(filters.jobRole)
    if (jobRole) {
        form.jobRoleId = jobRole.id
    }

    const group = toValue(filters.group)
    if (group) {
        form.groupId = group.id
    }

    const role = toValue(filters.role)
    if (role && role !== 'all') {
        form.role = role
    }

    const hasAccount = toValue(filters.hasAccount)
    if (hasAccount) {
        form.hasAccount = hasAccount
    }

    const lastActiveStatus = toValue(filters.lastActiveStatus)
    if (lastActiveStatus) {
        form.lastActiveStatus = lastActiveStatus
    }

    const showArchived = toValue(filters.showArchived)
    if (showArchived) {
        form.showArchived = showArchived
    }

    const tags = toValue(filters.tags)
    if (tags && tags.length > 0) {
        form.tags = tags.map(tag => tag.name)
    }

    const tagIds = toValue(filters.tagIds)
    if (tagIds && tagIds.length > 0) {
        form.tagIds = tagIds
    }

    const tagNames = toValue(filters.tagNames)
    if (tagNames && tagNames.length > 0) {
        form.tags = tagNames
    }

    const tagMatchMode = toValue(filters.tagMatchMode)
    if (tagMatchMode) {
        form.tagMatchMode = tagMatchMode
    }

    const hasNewAppLogin = toValue(filters.hasNewAppLogin)
    if (hasNewAppLogin) {
        form.hasNewAppLogin = hasNewAppLogin
    }

    const availableFrom = toValue(filters.availableFrom)
    if (availableFrom) {
        form.availableFrom = availableFrom
    }

    const availableTo = toValue(filters.availableTo)
    if (availableTo) {
        form.availableTo = availableTo
    }

    return form
}

export function useEmployeeQuery(siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EmployeeFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useQuery<{ employees: Employee[], pagination: Pagination }, Error>(employeeQueryOptions(siteId, filters, options))
}

export function useEmployeeQueryInfinite(siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EmployeeFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean>, staleTime?: number } = {}) {
    const queryFn = computed(() => {
        const siteIdValue = toValue(siteId)
        return siteIdValue ? ({ pageParam }: { pageParam?: number }) => {
            const form = filtersToForm(filters)

            form.perPage = toValue(filters.perPage) ?? 15
            form.page = pageParam ?? 1

            return fetch(siteIdValue, form)
        } : skipToken
    })
    return useInfiniteQuery({
        queryKey: employeeKeys.listInfinite(siteId, filters),
        queryFn: queryFn,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (lastPage.pagination.perPage === 5 || (nextPage > lastPage.pagination.totalPages)) {
                return undefined
            }

            return nextPage
        },
        getPreviousPageParam: (firstPage) => {
            const previousPage = firstPage.pagination.currentPage - 1

            if (previousPage < 1) {
                return undefined
            }

            return previousPage
        },
        enabled: options.enabled,
        staleTime: options.staleTime,
    })
}

export function employeeQueryOptions(siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EmployeeFilters, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    const queryFn = computed(() => {
        const siteIdValue = toValue(siteId)
        return siteIdValue ? async () => {
            return await fetch(siteIdValue, filtersToForm(filters))
        } : skipToken
    })
    return queryOptions<{ employees: Employee[], pagination: Pagination }, Error>({
        queryKey: employeeKeys.list(siteId, filters),
        queryFn: queryFn,
        placeholderData: keepPreviousData,
        enabled: options.enabled,
    })
}

export function useEmployeeDetailQuery(site: MaybeRefOrGetter<Site>, employeeId: MaybeRefOrGetter<EmployeeID | null | undefined>) {
    const queryFn = computed(() => {
        const siteIdValue = toValue(site)
        const employeeIdValue = toValue(employeeId)
        return siteIdValue && employeeIdValue ? async () => {
            return await show(siteIdValue, employeeIdValue)
        } : skipToken
    })

    return useQuery({
        queryKey: employeeKeys.detail(toValue(site)?.id, employeeId),
        queryFn: queryFn,
    })
}

const eligibleFiltersToForm = (filters: EligibleFilters): EligibleSearchForm => {
    const form: EligibleSearchForm = {
        shiftId: toValue(filters.shiftId),
        areaId: toValue(filters.areaId),
        skillIds: toValue(filters.skillIds) ?? undefined,
        jobRoleId: toValue(filters.jobRoleId) ?? undefined,
        startDate: toValue(filters.startDate) ?? null,
        endDate: toValue(filters.endDate) ?? null,
        tags: toValue(filters.tags)?.map(tag => tag.name) ?? toValue(filters.tagNames) ?? undefined,
        tagIds: toValue(filters.tagIds) ?? undefined,
        tagMatchMode: toValue(filters.tagMatchMode) ?? undefined,
        perPage: toValue(filters.perPage) ?? 15,
        page: toValue(filters.page) ?? 1,
    }

    const name = toValue(filters.name)
    if (name) {
        form.name = name
    }

    return form
}

export function useEligibleQuery(siteId: MaybeRefOrGetter<SiteID>, filters: EligibleFilters, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    return useQuery({
        queryKey: employeeKeys.listEligibleForShift(toValue(siteId), filters),
        queryFn: async () => {
            return await fetchEligible(toValue(siteId), eligibleFiltersToForm(filters))
        },
        placeholderData: keepPreviousData,
        enabled: options.enabled,
    })
}

export function useEligibleQueryInfinite(siteId: MaybeRefOrGetter<SiteID | undefined>, filters: EligibleFilters = {}, options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
    const queryFn = computed(() => {
        const siteIdValue = toValue(siteId)
        return siteIdValue ? ({ pageParam }: { pageParam?: number }) => {
            const form = eligibleFiltersToForm(filters)

            form.perPage = toValue(filters.perPage) ?? 15
            form.page = pageParam ?? 1

            return fetchEligible(siteIdValue, form)
        } : skipToken
    })
    return useInfiniteQuery({
        queryKey: employeeKeys.listEligibleForShift(siteId, filters),
        queryFn: queryFn,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (lastPage.pagination.perPage === 5 || (nextPage > lastPage.pagination.totalPages)) {
                return undefined
            }

            return nextPage
        },
        getPreviousPageParam: (firstPage) => {
            const previousPage = firstPage.pagination.currentPage - 1

            if (previousPage < 1) {
                return undefined
            }

            return previousPage
        },
        enabled: options.enabled,
    })
}
